import { Box } from '@wix/design-system';
import { ProductUtils } from '@wix/package-picker-shared';
import {
  useIsSeasonalSale,
  useIsStudioNewUi,
} from '@wix/package-picker-shared-studio';
import { FeaturesGrid as PlatformFeaturesGrid } from '@wix/premium-purchase-platform/@components';
import React from 'react';
import s from './FeaturesGrid.scss';
import { FeaturesGridGroup } from './FeaturesGridGroup';
import { FeaturesGridProductHeader } from './FeaturesGridProductHeader';

interface FeaturesGridProps {
  innerRef?: React.RefObject<HTMLDivElement>;
}

export const FeaturesGrid = ({ innerRef }: FeaturesGridProps) => {
  const isStudioNewUi = useIsStudioNewUi();
  const isSeasonalSale = useIsSeasonalSale();

  const grid = (
    <PlatformFeaturesGrid.Root
      innerRef={innerRef}
      sortProducts={(products) => ProductUtils.sortProducts({ products })}
    >
      <PlatformFeaturesGrid.Header
        marginTop={28}
        {...(isStudioNewUi && {
          cornerCellProps: {
            border: 'none',
            marginTop: isSeasonalSale ? undefined : '-28px',
          },
        })}
      >
        {({ products, recommendations }) => {
          const sortedProducts = ProductUtils.sortProducts({ products });
          const recommendedIndex =
            sortedProducts?.findIndex((product) =>
              recommendations.isRecommended(product),
            ) ?? -1;

          return (
            <>
              {sortedProducts?.map((product) => {
                if (!product.productId) {
                  return null;
                }

                return (
                  <FeaturesGridProductHeader
                    key={product.productId}
                    productId={product.productId!}
                    isRecommended={recommendations.isRecommended(product)}
                  />
                );
              })}
              {isStudioNewUi && recommendedIndex >= 0 && (
                <Box
                  className={s.recommendedColumn}
                  style={{ gridColumn: `${recommendedIndex + 2} / span 1` }}
                />
              )}
            </>
          );
        }}
      </PlatformFeaturesGrid.Header>
      <PlatformFeaturesGrid.Content>
        {({ products, namespaces, recommendations }) => {
          const recommendedProductId = products?.find((product) =>
            recommendations.isRecommended(product),
          )?.productId;

          return namespaces?.map((namespace) => (
            <FeaturesGridGroup
              key={namespace.id}
              group={namespace}
              products={products}
              recommendedProductId={recommendedProductId}
            />
          ));
        }}
      </PlatformFeaturesGrid.Content>
    </PlatformFeaturesGrid.Root>
  );

  return isStudioNewUi ? (
    <Box direction="vertical" width="100%" className={s.comparisonTable}>
      {grid}
    </Box>
  ) : (
    grid
  );
};
